import { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  useToast,
  Select,
  Spinner,
  Button,
  Icon,
} from '@chakra-ui/react';
import { FaHistory } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from './useNotifications';
import { NotificationItem } from './components/NotificationItem';
import { NotificationFilters } from './components/NotificationFilters';
import { ActivityLogModal } from '../ActivityLogs/components/ActivityLogModal';
import { Notification as EOSNotification } from '../types';
import { Timestamp } from 'firebase/firestore';

/**
 * Notifications Page - View and manage system notifications
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function NotificationsPage() {
  const history = useHistory();
  const { currentUser } = useAuth();
  const toast = useToast();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const {
    notifications,
    users,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications(currentUser?.uid, selectedUserId);

  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isActivityLogOpen, setIsActivityLogOpen] = useState(false);

  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    let filtered = notifications;

    // Filter by user if selected
    if (selectedUserId) {
      filtered = filtered.filter((n) => n.triggeredByUserId === selectedUserId);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter((n) => n.type === typeFilter);
    }

    // Filter by status
    if (statusFilter === 'unread') {
      filtered = filtered.filter((n) => {
        const read = n.read;
        return !read || (read !== true && !(read instanceof Timestamp));
      });
    } else if (statusFilter === 'read') {
      filtered = filtered.filter((n) => {
        const read = n.read;
        return read === true || (read instanceof Timestamp);
      });
    }

    return filtered;
  }, [notifications, typeFilter, statusFilter, selectedUserId]);

  const handleNotificationClick = (notification: EOSNotification) => {
    // Mark as read if unread
    const read = notification.read;
    if (!read || (read !== true && !(read instanceof Timestamp))) {
      markAsRead(notification.id);
    }

    // Navigate to related content if path exists
    if (notification.path) {
      history.push(notification.path);
    } else if (notification.route) {
      history.push(notification.route);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
      toast({
        title: 'Success',
        description: 'Notification marked as read',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to mark notification as read',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast({
        title: 'Success',
        description: 'All notifications marked as read',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to mark all notifications as read',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (notificationId: string) => {
    try {
      await deleteNotification(notificationId);
      toast({
        title: 'Success',
        description: 'Notification deleted',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete notification',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Heading size="lg" color={headingColor} mb={2}>
            Notifications
          </Heading>
          <Text color={subheadingColor}>
            {unreadCount > 0
              ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
              : 'All caught up!'}
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={FaHistory} />}
          onClick={() => setIsActivityLogOpen(true)}
          variant="outline"
          size="sm"
        >
          User Activity Log
        </Button>
      </Flex>

      {/* Filters */}
      <Box mb={6}>
        <Flex gap={3} wrap="wrap" mb={4} align="flex-end">
          <Box>
            <Text fontSize="sm" mb={1}>
              Filter by User:
            </Text>
            <Select
              value={selectedUserId || 'all'}
              onChange={(e) => setSelectedUserId(e.target.value === 'all' ? null : e.target.value)}
              size="sm"
              width="200px"
            >
              <option value="all">All Users</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>
        <NotificationFilters
          typeFilter={typeFilter}
          statusFilter={statusFilter}
          onTypeFilterChange={setTypeFilter}
          onStatusFilterChange={setStatusFilter}
          onMarkAllAsRead={handleMarkAllAsRead}
          unreadCount={unreadCount}
        />
      </Box>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <Box textAlign="center" py={12}>
          <Text color={subheadingColor}>
            {notifications.length === 0
              ? 'No notifications yet'
              : 'No notifications match your filters'}
          </Text>
        </Box>
      ) : (
        <VStack spacing={3} align="stretch">
          {filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={() => handleMarkAsRead(notification.id)}
              onDelete={() => handleDelete(notification.id)}
              onClick={() => handleNotificationClick(notification)}
            />
          ))}
        </VStack>
      )}

      {/* Activity Log Modal */}
      <ActivityLogModal
        isOpen={isActivityLogOpen}
        onClose={() => setIsActivityLogOpen(false)}
      />
    </Container>
  );
}
